import { CashRegisterModelType } from '@teinor/erp/types/company/cashRegister'
import { CashRegisterMoneyModelAdd } from '@teinor/erp/types/company/cashRegister/cashRegisterMoney'
import { operationTypesInversed } from '@teinor/erp/types/company/cashRegister/cashRegisterMoney/cashRegisterMoneyLog'
import { relatedTypesInversed } from '@teinor/erp/types/company/config/paymentType'
import { useCallback, useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { MediumModalCreateEdit } from '../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../../../../baseComponents/ModalsLayouts/types'
import { RowData } from '../../../../../../../../baseComponents/TwinTable/types'
import { FormElement, FormRender } from '../../../../../../../../forms/FormRenderer/types'
import { InputWithLabelMargin } from '../../../../../../../../forms/Input'
import PaymentTypesAndSellsRegisterTPV from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellTicketTPVLayout/components/PaymentTypesRegisterTPV'
import { PaymentTypeModelTypeExtendedTPV } from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellTicketTPVLayout/types'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import { displayDateWithFormattedHours } from '../../../../../../../../utils/globals/date'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { returnClassOfSpanTemplate } from '../../../../../../../../utils/globals/tailwind'
import withLoading from '../../../../../../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../../utils/reducers'
import { PaymentTypesPayload } from '../../../../../../../../utils/reducers/payments/paymentTypes'
import CashRegisterMoneyTable from '../../../../../TPV/TPVDashboard/ModalCashRegister/CashRegisterMoneyTable'
import { PaymentTypesRegisterMoneyTPV } from '../../../../../TPV/TPVDashboard/types'

type ModalCashRegisterInfoProps = ModalEditComponentProps & ModalCashRegisterInfoLogicProps & {
}

interface CashRegisterModelTypeExtended extends CashRegisterModelType {
    companyName: string
    workingCenterName: string
    workingCenterStationName: string
}

const ModalCashRegisterInfo: React.FC<ModalCashRegisterInfoProps> = ({ ...rest }) => {
    const { t } = useTwinTranslation()
    const { paymentTypesRegisterMoney, setPaymentTypesRegisterMoney } = useModalCashRegisterInfoLogic({ allRowData: rest.allRowData, ...rest })
    let extraFields: FormElement<keyof CashRegisterModelTypeExtended>[] = []
    if (rest?.allRowData?.WorkingCenterStationId) {
        extraFields.push({
            name: 'workingCenterStationName',
            label: t('workStation', 'Puesto'),
            component: 'TextField',
            value: rest?.allRowData?.WorkingCenterStation?.name,
        })
    }
    const fields: FormRender<CashRegisterModelTypeExtended> = [
        {
            cols: 4,
            title: t('store', 'Tienda'),
            elements: [
                {
                    name: 'workingCenterName',
                    label: t('name', 'Nombre'),
                    component: 'TextField',
                    value: rest?.allRowData?.WorkingCenter?.name,
                },
                ...extraFields,
                {
                    name: 'OpenEmployeeId',
                    label: t('openEmployee', 'Empleado de apertura'),
                    component: 'TextField',
                    value: rest?.allRowData?.OpenEmployee?.fullname_short,
                },
                {
                    name: 'CloseEmployeeId',
                    label: t('closeEmployee', 'Empleado de cierre'),
                    component: 'TextField',
                    value: rest?.allRowData?.CloseEmployee?.fullname_short,
                },
            ]
        },
        {
            cols: 5,
            title: t('opening', 'Apertura'),
            elements: [
                {
                    name: 'full_assigned_number',
                    label: t('number', 'Número'),
                    component: 'TextField',
                    value: rest.allRowData?.full_assigned_number || '-',
                },
                {
                    name: 'createdAt',
                    label: t('openingDate', 'Fecha de apertura'),
                    component: 'TextField',
                    value: displayDateWithFormattedHours(new Date(rest.allRowData?.createdAt)),
                    className: returnClassOfSpanTemplate(2)
                },
                {
                    name: 'endDate',
                    label: t('closeDate', 'Fecha de cierre'),
                    component: 'TextField',
                    value: displayDateWithFormattedHours(new Date(rest.allRowData?.endDate)),
                    className: returnClassOfSpanTemplate(2)
                },
            ]
        },
    ]
    return (
        <MediumModalCreateEdit fields={fields} url={''} translations={{
            title: t('cashRegisterLong', 'Registro de caja'),
            button: ''
        }} haveButtonPermissions={false} {...rest} className='modal_cash_register notFlexAutoFormRender' >
            <CashRegisterNotes notes={paymentTypesRegisterMoney?.notes || null}/>
            <CashRegisterMoneyTable isCloseCashRegister={false} isReadOnly={true} {...{ cashRegisterMoneyRows: paymentTypesRegisterMoney?.cashRegisterMoneyRows || [], setPaymentTypesRegisterMoney }} />
            <div className='flex flex-auto flex-col mt-20'>
                <PaymentTypesAndSellsRegisterTPV paymentTypesTPV={paymentTypesRegisterMoney?.allPaymentMethodsTotals || []} />
            </div>
        </MediumModalCreateEdit>
    )
}

interface ModalCashRegisterInfoLogicProps extends ReduxPaymentTypes {
    allRowData?: RowData
}

interface PaymentTypesRegisterMoneyTPVExtended extends PaymentTypesRegisterMoneyTPV {
    notes?: TwinDictionary | null
}

const useModalCashRegisterInfoLogic = ({ allRowData, paymentTypes }: ModalCashRegisterInfoLogicProps) => {
    const [paymentTypesRegisterMoney, setPaymentTypesRegisterMoney] = useState<PaymentTypesRegisterMoneyTPVExtended | null>(null)

    const getAllCashRegisterMoneysByCashRegister = useCallback(async () => {
        if (allRowData?.id) {
            const result = await twinFetchPostJSON('/api/app/order/tpv/cashRegister/cashRegisterMoney/getAllCashRegisterMoneyLogsByCashRegister', { where: { 'CashRegisterMoney.CashRegisterId': allRowData?.id } })
            if (result) {
                const dictCashRegisterMoneys: TwinDictionary = {}
                const myCashRegisterMoneyRow: CashRegisterMoneyModelAdd[] = []
                const notes: TwinDictionary = {}
                for (const element of result) {
                    const cashRegisterMoney = JSON.parse(JSON.stringify(element?.CashRegisterMoney))
                    if (!dictCashRegisterMoneys?.[cashRegisterMoney.PaymentTypeId]) {
                        delete element.CashRegisterMoney
                        const myCashRegisterMoneyLogs = []
                        myCashRegisterMoneyLogs.push(element)
                        dictCashRegisterMoneys[cashRegisterMoney.PaymentTypeId] = { ...cashRegisterMoney, CashRegisterMoneyLogs: myCashRegisterMoneyLogs, money: element.endMoney, endMoney: element.endMoney }
                    } else {
                        dictCashRegisterMoneys[cashRegisterMoney.PaymentTypeId]['CashRegisterMoneyLogs'].push(element)
                    }
                    if (element?.extraData?.notes){
                        if (element.operationType === operationTypesInversed['cashOpening']){
                            notes['openNotes'] = element.extraData.notes
                        } else if (element.operationType === operationTypesInversed['cashClosing']) {
                            notes['closeNotes'] = element.extraData.notes
                        }
                    }
                }
                const myResult: PaymentTypeModelTypeExtendedTPV[] = []
                for (const key in paymentTypes?.activeTpv) {
                    const element = paymentTypes?.activeTpv[parseInt(key)]
                    if (element) {
                        myResult.push({ ...element, CashRegisterMoney: dictCashRegisterMoneys?.[element.id] })
                    }
                    if (element?.relatedType === relatedTypesInversed['cash']) {
                        myCashRegisterMoneyRow.push({ ...dictCashRegisterMoneys?.[element.id], PaymentType: element })
                    }
                }
                setPaymentTypesRegisterMoney({ allPaymentMethodsTotals: myResult, cashRegisterMoneyRows: myCashRegisterMoneyRow, notes })
            }
        }

    }, [setPaymentTypesRegisterMoney, allRowData, paymentTypes?.activeTpv])

    useEffect(() => {
        getAllCashRegisterMoneysByCashRegister()
    }, [getAllCashRegisterMoneysByCashRegister])

    return { paymentTypesRegisterMoney, setPaymentTypesRegisterMoney }
}

interface CashRegisterNotesProps {
    notes: TwinDictionary | null
}

const CashRegisterNotes: React.FC<CashRegisterNotesProps> = ({notes}) => {
    const { t } = useTwinTranslation()
    if (!notes || !Object.keys(notes)?.length) {
        return null
    }
    return (<div className='flex w-full mt-10 flex-col gap-y-5'>
        {notes?.openNotes ? <InputWithLabelMargin label={t('openNotes', 'Nota de apertura')} value={notes?.openNotes} readOnly={true}  /> : null}
        {notes?.closeNotes ? <InputWithLabelMargin label={t('closeNotes', 'Nota de cierre')} value={notes?.closeNotes}  readOnly={true} /> : null}
    </div>)
}


const paymentTypesDispatch = {
    setPaymentTypes: (payload: PaymentTypesPayload) => ({ type: 'CHANGE_PAYMENTTYPES', payload }),
}

export type ReduxPaymentTypes = ConnectedProps<typeof PaymentTypesConnect>
const mapPaymentTypesConnector = (state: AllReduxPayloads) => ({ paymentTypes: state.paymentTypes })
const PaymentTypesConnect = connect(mapPaymentTypesConnector, paymentTypesDispatch)
const PaymentTypesConnectLoading = withLoading(ModalCashRegisterInfo, [{ fetchUrl: '/api/app/config/payment/paymentType/getAllPaymentTypes', propName: 'paymentTypes', setFunctionName: 'setPaymentTypes' }])
const ModalCashRegisterInfoConnect = PaymentTypesConnect(PaymentTypesConnectLoading)

export default ModalCashRegisterInfoConnect